import { create } from 'zustand';
import { logger } from '@/features/help/utils/logger';
import { FormPage } from '../types/forms';
import { useValidationStore } from './validation.store';
import { formAutosaveService } from '../services/form-autosave.service';
import { useApplicationStore } from './application.store';
import { useSignerStore } from './signer.store';
import { toast } from 'react-hot-toast';
import { syncDomValuesWithState } from '../utils/validation.utils';

interface NavigationStore {
  activePage: number;
  isNavigating: boolean;

  // Navigation actions
  handleNextPage: (formElement?: HTMLDivElement | null, mappingShortId?: string) => Promise<void>;
  handlePreviousPage: () => void;
  setActivePage: (page: number) => void;
  navigateToFieldPage: (fieldId: string) => void;

  // Navigation-related computed values
  getCurrentPage: () => FormPage | null;
  getVisiblePages: () => FormPage[];
  getTotalPages: () => number;
}

export const useNavigationStore = create<NavigationStore>((set, get) => ({
  activePage: 0,
  isNavigating: false,

  handleNextPage: async (formElement?: HTMLDivElement | null, mappingShortId?: string) => {
    const { activePage, isNavigating } = get();
    
    // Prevent double-clicks while already navigating
    if (isNavigating) {
      return;
    }
    
    const { structure, handleFieldValueChange } = useApplicationStore.getState();

    if (!structure) {
      return;
    }

    const currentPage = get().getCurrentPage();
    if (!currentPage) {
      return;
    }

    // Set navigating state for loading indicator
    set({ isNavigating: true });

    try {
      // Synchronize DOM values with React state before validation (handles browser autofill)
      if (formElement) {
        const syncCount = await syncDomValuesWithState(
          formElement,
          structure,
          handleFieldValueChange
        );

        if (syncCount > 0) {
          logger.info('Navigation Store', 'handleNextPage: Synchronized autofilled values', {
            fieldsUpdated: syncCount,
            pageId: currentPage.id,
          });
        }
      }

      // Re-fetch fresh state after DOM sync to ensure validation uses updated values
      const {
        structure: freshStructure,
        viewableFields: freshViewableFields,
        files: freshFiles,
      } = useApplicationStore.getState();

      if (!freshStructure) {
        set({ isNavigating: false });
        return;
      }

      const validationStore = useValidationStore.getState();
      const isPageValid = await validationStore.validatePage(
        currentPage.id,
        freshStructure,
        freshViewableFields,
        useApplicationStore.getState().editableFields,
        freshFiles
      );

      if (!isPageValid) {
        toast.error('Please correct the errors on this page before proceeding.');
        const updatedInvalidFields = useValidationStore.getState().invalidFields;
        const updatedInvalidFieldsPageMap = useValidationStore.getState().invalidFieldsPageMap;

        const firstInvalidFieldOnCurrentPage = updatedInvalidFields.find(
          (fieldId) => updatedInvalidFieldsPageMap[fieldId] === currentPage.id
        );

        if (firstInvalidFieldOnCurrentPage) {
          get().navigateToFieldPage(firstInvalidFieldOnCurrentPage);
        } else {
          logger.warn(
            'Navigation Store',
            'handleNextPage: Page invalid, but no specific invalid field found on this page in the map. Focusing page.',
            { pageId: currentPage.id }
          );
          set({ activePage });
        }
        set({ isNavigating: false });
        return;
      }

      // After validation succeeds, navigate FIRST then perform autosave (optimistic)
      const visiblePages = get().getVisiblePages();
      const isLastPage = activePage >= visiblePages.length - 1;

      // Navigate immediately (optimistic UI)
      if (activePage < visiblePages.length - 1) {
        const nextPage = activePage + 1;
        set({ activePage: nextPage, isNavigating: false });
      } else {
        set({ isNavigating: false });
        return;
      }

      // Perform autosave AFTER navigation (non-blocking) for non-last pages
      // Skip autosave entirely in pdfOnly mode (purpose is just PDF generation, no CRM updates)
      const { pdfOnly } = useApplicationStore.getState();
      if (!isLastPage && !pdfOnly) {
        const appState = useApplicationStore.getState();
        const { crmId, setCrmId } = appState;

        if (!mappingShortId) {
          logger.warn('Navigation Store', 'Autosave skipped: missing mappingShortId');
        } else if (!crmId) {
          // First-time autosave (create) - fire-and-forget but handle crmId when it returns
          formAutosaveService.autosave(mappingShortId)
            .then((res) => {
              if (res?.success && res?.crmId) {
                setCrmId(res.crmId);
                try {
                  const url = new URL(window.location.href);
                  url.searchParams.set('crmId', String(res.crmId));
                  // signerIndex is 1 by default for primary signer
                  url.searchParams.set('signerIndex', '1');
                  window.history.replaceState({}, '', url.toString());
                  logger.info('Navigation Store', 'Updated URL with crmId and signerIndex after optimistic navigation');
                } catch (e) {
                  logger.warn('Navigation Store', 'Failed to update URL search params after autosave', { e });
                }
              } else {
                logger.warn('Navigation Store', 'Autosave did not return crmId');
              }
            })
            .catch((e) => {
              logger.warn('Navigation Store', 'Autosave create failed (after navigation)', { e });
            });
        } else {
          // Fire-and-forget autosave for updates
          formAutosaveService.autosave(mappingShortId).catch((e) =>
            logger.warn('Navigation Store', 'Autosave update failed (non-blocking)', { e })
          );
        }
      }
    } catch (e) {
      logger.warn('Navigation Store', 'handleNextPage threw an error', { e });
      set({ isNavigating: false });
    }
  },

  handlePreviousPage: () => {
    const { activePage } = get();

    if (activePage > 0) {
      set({ activePage: activePage - 1 });
    }
  },

  setActivePage: (page: number) => {
    const visiblePages = get().getVisiblePages();
    if (visiblePages.length === 0) return;
    if (page >= 0 && page < visiblePages.length) {
      set({ activePage: page });
    }
  },

  navigateToFieldPage: (fieldId: string) => {
    const { structure } = useApplicationStore.getState();
    if (!structure) return;

    const visiblePages = get().getVisiblePages();

    // Find which page contains the field
    let targetPageIndex = -1;
    for (let pageIndex = 0; pageIndex < visiblePages.length; pageIndex++) {
      const page = visiblePages[pageIndex];
      const hasField = page.sections.some((section) =>
        section.fields.some((field) => field.id === fieldId)
      );
      if (hasField) {
        targetPageIndex = pageIndex;
        break;
      }
    }

    if (targetPageIndex !== -1) {
      get().setActivePage(targetPageIndex);
    } else {
      logger.warn('Navigation Store', 'Field not found in any visible page', {
        fieldId,
        visiblePageCount: visiblePages.length,
      });
    }
  },

  getCurrentPage: () => {
    const { activePage } = get();
    const { structure } = useApplicationStore.getState();
    if (!structure?.pages) return null;
    const visiblePages = get().getVisiblePages();
    return visiblePages[activePage] ?? null;
  },

  getVisiblePages: () => {
    const { structure, viewableFields, editableFields, pdfOnly, allowAdditionalSignerDocUpload } = useApplicationStore.getState();
    const { currentSigner } = useSignerStore.getState();
    if (!structure?.pages) return [];
    return structure.pages.filter((page) => {
      // Hide document-upload page in pdfOnly mode
      if (pdfOnly && page.id === 'document-upload') {
        return false;
      }
      // Hide document-upload page for additional signers (index > 1) unless allowed by broker setting
      if (page.id === 'document-upload' && currentSigner > 1 && !allowAdditionalSignerDocUpload) {
        return false;
      }
      return page.sections.some((section) =>
        section.fields.some(
          (field) => viewableFields.includes(field.id) || editableFields.includes(field.id)
        )
      );
    });
  },

  getTotalPages: () => {
    const visiblePages = get().getVisiblePages();
    return visiblePages.length;
  },
}));
